/**
 * Estate Sprawl — where version chaos is costing the estate most.
 *
 * Two numbers per family, because they answer different questions:
 *   • version debt (versions − 1)      "which apps are out of control?"
 *   • devices not on latest             "where does a cleanup pay off?"
 * A 100-version family with 99% of devices on the newest build is a cheap
 * long tail; a 5-version family split evenly across 2,000 devices is the
 * real standardisation failure. The quadrant plots one against the other,
 * split on the estate medians, so each corner is a different piece of
 * advice. The leaderboard beneath is sortable on either axis or on the
 * combined sprawl score.
 *
 * Vulnerability is an overlay, not an input: red marks a family with a
 * vulnerable version in the estate, which is the hand-off to the Fix Queue.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea, BarChart, Bar, Cell, LabelList,
} from "recharts";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, Layers, ArrowUpDown, ShieldAlert, ExternalLink, Info,
} from "lucide-react";

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";
const BTN_GHOST =
  "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10";

const SEVERITY_COLOR = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-600 text-white",
  MEDIUM: "bg-yellow-600 text-white",
  LOW: "bg-blue-600 text-white",
};

const QUADRANT_META = {
  standardise: { label: "Standardise now", blurb: "Many versions and many devices behind — the consolidation programme.", color: "#ef4444", chip: "bg-red-600 text-white" },
  quick_win:   { label: "Quick win",       blurb: "Few versions, many devices behind — one push clears it.",             color: "#10b981", chip: "bg-emerald-600 text-white" },
  long_tail:   { label: "Long tail",       blurb: "Many versions, few devices behind — batch it as housekeeping.",       color: "#f59e0b", chip: "bg-amber-500 text-black" },
  healthy:     { label: "Healthy",         blurb: "Few versions, few devices behind — leave it alone.",                   color: "#94a3b8", chip: "bg-white/15 text-white" },
};
const QUADRANT_ORDER = ["standardise", "quick_win", "long_tail", "healthy"];

const COLOR_VULN = "#f87171";   // red-400
const COLOR_CLEAN = "#67e8f9";  // cyan-300

const TOOLTIP_STYLE = {
  background: "rgba(15, 23, 42, 0.95)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 12,
  padding: "8px 12px",
};

const KpiTile = ({ label, value, sub, tone = "default", testId }) => {
  const toneClasses = {
    default: "text-white", danger: "text-red-300", warning: "text-orange-300", good: "text-emerald-300",
  }[tone] || "text-white";
  return (
    <div className={`${CARD_BG} p-4`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
      <div className={`text-2xl font-semibold leading-tight ${toneClasses}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
};

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

// Share bar: how much of the family sits on the latest build vs behind it.
const SpreadBar = ({ row }) => {
  const total = row.total_devices || 0;
  const latest = total ? (row.devices_on_latest / total) * 100 : 0;
  return (
    <div className="min-w-[140px]">
      <div className="h-2 rounded-sm overflow-hidden bg-white/10 flex" title={`${row.devices_on_latest} on ${row.latest_version} · ${row.devices_not_on_latest} behind`}>
        <div className="h-full bg-emerald-400" style={{ width: `${latest}%` }} />
        <div className="h-full bg-amber-400" style={{ width: `${100 - latest}%` }} />
      </div>
      <div className="text-[10px] text-white/55 mt-1 tabular-nums">
        {(row.devices_not_on_latest || 0).toLocaleString()} of {total.toLocaleString()} behind
        {row.exact === false && (
          <span className="ml-1 text-white/40" title="Estimated from stored install counts — a machine carrying two builds is counted twice. Click the row for measured numbers.">
            (est.)
          </span>
        )}
      </div>
    </div>
  );
};

const QuadrantTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  const q = QUADRANT_META[r.quadrant] || QUADRANT_META.healthy;
  return (
    <div style={TOOLTIP_STYLE}>
      <div className="font-semibold mb-1">{r.family_name}</div>
      <div>{r.version_count} versions · {r.fragmentation != null ? pct(r.fragmentation) : "—"} fragmented</div>
      <div>{(r.devices_not_on_latest || 0).toLocaleString()} of {(r.total_devices || 0).toLocaleString()} devices behind {r.latest_version}</div>
      {r.has_vulnerable && (
        <div className="text-red-300 mt-1">{r.vulnerable_version_count} vulnerable version{r.vulnerable_version_count === 1 ? "" : "s"}{r.worst_severity ? ` · ${r.worst_severity}` : ""}</div>
      )}
      <div className="mt-1" style={{ color: q.color }}>{q.label}</div>
    </div>
  );
};

const HistogramTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].payload;
  return (
    <div style={TOOLTIP_STYLE}>
      <div className="font-mono font-semibold mb-1">{v.version || "no version"}</div>
      <div>{(v.device_count || 0).toLocaleString()} devices</div>
      {v.is_vulnerable && <div className="text-red-300">Vulnerable{v.highest_severity ? ` · ${v.highest_severity}` : ""} · {v.cve_count} CVE{v.cve_count === 1 ? "" : "s"}</div>}
    </div>
  );
};

const SORT_KEYS = {
  sprawl_score: "Sprawl score",
  version_count: "Versions",
  devices_not_on_latest: "Devices behind",
  fragmentation: "Fragmentation",
};

const EstateSprawlTab = () => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sortKey, setSortKey] = useState("sprawl_score");
  const [quadrantFilter, setQuadrantFilter] = useState("all");
  const [vulnOnly, setVulnOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/sprawl`, { headers, params: { limit: 2000 } });
      setData(r.data || null);
    } catch (err) {
      console.warn("sprawl load failed", err);
      toast.error("Failed to load estate sprawl");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const openFamily = useCallback(async (name) => {
    setSelected(name);
    setDetail(null);
    setDetailLoading(true);
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/sprawl/${encodeURIComponent(name)}`, { headers });
      setDetail(r.data || null);
    } catch (err) {
      toast.error("Failed to load version breakdown");
    } finally {
      setDetailLoading(false);
    }
  }, [authHeaders]);

  // Recharts hands a Scatter click the point node, which carries the row as
  // ``payload`` (and, on older majors, spreads it too). Accept both.
  const onPointClick = useCallback((p) => {
    const row = p?.payload || p;
    if (row?.family_name) openFamily(row.family_name);
  }, [openFamily]);

  const families = data?.families || [];
  const summary = data?.summary || {};
  const splits = data?.splits || {};
  const campaign = data?.campaign_top10 || {};

  const filtered = useMemo(() => {
    let rows = families;
    if (quadrantFilter !== "all") rows = rows.filter((r) => r.quadrant === quadrantFilter);
    if (vulnOnly) rows = rows.filter((r) => r.has_vulnerable);
    return rows.slice().sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0) || (a.family_name || "").localeCompare(b.family_name || ""));
  }, [families, quadrantFilter, vulnOnly, sortKey]);

  // The scatter draws the filtered set (capped so a 2,000-family estate stays
  // responsive) — plus the selected family, so it never vanishes on click.
  const plotted = useMemo(() => {
    const cap = 400;
    const base = filtered.slice(0, cap);
    if (selected && !base.some((r) => r.family_name === selected)) {
      const sel = families.find((r) => r.family_name === selected);
      if (sel) base.push(sel);
    }
    return base;
  }, [filtered, families, selected]);
  const plotVuln = plotted.filter((r) => r.has_vulnerable);
  const plotClean = plotted.filter((r) => !r.has_vulnerable);

  const xMax = Math.max(2, ...families.map((r) => r.version_count || 2));
  const yMax = Math.max(1, ...families.map((r) => r.devices_not_on_latest || 0));
  const xSplit = Math.max(splits.version_count || 2, 2);
  const ySplit = splits.devices_not_on_latest || 0;

  // Oldest → newest, left to right, so the histogram reads as release order.
  const histogram = useMemo(
    () => (detail?.versions || []).slice().reverse().map((v) => ({ ...v, label: v.version || "—" })),
    [detail]
  );

  return (
    <div className="space-y-4" data-testid="estate-sprawl-tab">
      {/* KPI strip */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Version debt"
          value={(summary.version_debt || 0).toLocaleString()}
          sub={`versions that should not exist, across ${(summary.multi_version_families || 0).toLocaleString()} families`}
          tone={summary.version_debt > 0 ? "warning" : "good"}
          testId="kpi-sprawl-debt"
        />
        <KpiTile
          label="Devices behind latest"
          value={`${(summary.devices_not_on_latest || 0).toLocaleString()}${summary.all_exact === false ? " ~" : ""}`}
          sub={
            summary.all_exact === false
              ? `measured for the top ${summary.exact_families || 0} families, estimated for the rest · summed across families`
              : "summed across families — one machine can be behind on several apps"
          }
          testId="kpi-sprawl-behind"
        />
        <KpiTile
          label={`Sprawl families (> ${summary.sprawl_threshold || 5} versions)`}
          value={(summary.sprawl_families || 0).toLocaleString()}
          sub={`${summary.risky_sprawl_families || 0} carry a vulnerable version (${summary.risky_sprawl_pct || 0}%)`}
          tone={summary.risky_sprawl_families > 0 ? "danger" : summary.sprawl_families > 0 ? "warning" : "default"}
          testId="kpi-sprawl-families"
        />
        <KpiTile
          label="Mean fragmentation"
          value={pct(summary.mean_fragmentation)}
          sub="share of devices NOT on a family's most-installed version — 0% is standardised"
          testId="kpi-sprawl-frag"
        />
      </div>

      {/* Campaign preview */}
      {campaign.families > 0 && (
        <div className={`${CARD_BG} px-4 py-3 text-sm text-white/85 flex items-start gap-2`} data-testid="sprawl-campaign">
          <Layers className="w-4 h-4 text-cyan-300 mt-0.5 shrink-0" />
          <div>
            Consolidating the top <b>{campaign.families}</b> families by sprawl score retires{" "}
            <b>{(campaign.versions_retired || 0).toLocaleString()}</b> versions across{" "}
            <b>{(campaign.devices_moved || 0).toLocaleString()}</b> devices
            {campaign.vulnerable_versions_cleared > 0 && (
              <> and clears <b className="text-red-300">{campaign.vulnerable_versions_cleared}</b> vulnerable version{campaign.vulnerable_versions_cleared === 1 ? "" : "s"}</>
            )}.
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className={`${CARD_BG} p-3 flex flex-wrap items-center gap-2 justify-between`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-white/60 uppercase tracking-wide">Quadrant</span>
          <Button size="sm" variant={quadrantFilter === "all" ? "default" : "outline"} onClick={() => setQuadrantFilter("all")}
            className={quadrantFilter === "all" ? "bg-cyan-600 hover:bg-cyan-500 text-white" : BTN_GHOST} data-testid="sprawl-quadrant-all">
            All
          </Button>
          {QUADRANT_ORDER.map((q) => {
            const m = QUADRANT_META[q];
            const n = summary.quadrant_counts?.[q] || 0;
            return (
              <Button key={q} size="sm" variant={quadrantFilter === q ? "default" : "outline"} onClick={() => setQuadrantFilter(q)}
                title={m.blurb}
                className={quadrantFilter === q ? "bg-cyan-600 hover:bg-cyan-500 text-white" : BTN_GHOST} data-testid={`sprawl-quadrant-${q}`}>
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: m.color }} />
                {m.label} <span className="ml-1 text-white/50">{n}</span>
              </Button>
            );
          })}
          <Button size="sm" variant={vulnOnly ? "default" : "outline"} onClick={() => setVulnOnly((v) => !v)}
            className={vulnOnly ? "bg-red-600 hover:bg-red-500 text-white" : BTN_GHOST} data-testid="sprawl-vuln-only">
            <ShieldAlert className="w-4 h-4 mr-1" /> Vulnerable only
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={load} className={BTN_GHOST} data-testid="sprawl-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Quadrant */}
        <div className={`col-span-12 xl:col-span-7 min-w-0 ${CARD_BG} p-4`} data-testid="sprawl-quadrant-chart">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-sm font-semibold text-white">Where cleanup pays off</div>
            <div className="text-[11px] text-white/50 inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> Split on estate medians · bubble = installs · click to drill down
            </div>
          </div>
          {families.length === 0 ? (
            <div className="h-[380px] flex items-center justify-center text-sm text-white/50">
              {loading ? "Loading…" : "No multi-version families — nothing to consolidate."}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <ScatterChart margin={{ top: 16, right: 24, bottom: 28, left: 8 }}>
                <ReferenceArea x1={xSplit} x2={xMax} y1={ySplit} y2={yMax} fill="#ef4444" fillOpacity={0.07}
                  label={{ value: "STANDARDISE NOW", position: "insideTopRight", fill: "#fca5a5", fontSize: 11, fontWeight: 700 }} />
                <ReferenceArea x1={2} x2={xSplit} y1={ySplit} y2={yMax} fill="#10b981" fillOpacity={0.07}
                  label={{ value: "QUICK WINS", position: "insideTopLeft", fill: "#6ee7b7", fontSize: 11, fontWeight: 700 }} />
                <ReferenceArea x1={xSplit} x2={xMax} y1={0} y2={ySplit} fill="#f59e0b" fillOpacity={0.07}
                  label={{ value: "LONG TAIL", position: "insideBottomRight", fill: "#fcd34d", fontSize: 11, fontWeight: 700 }} />
                <ReferenceArea x1={2} x2={xSplit} y1={0} y2={ySplit} fill="#94a3b8" fillOpacity={0.04}
                  label={{ value: "HEALTHY", position: "insideBottomLeft", fill: "rgba(255,255,255,0.45)", fontSize: 11, fontWeight: 700 }} />
                <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  type="number" dataKey="version_count" name="Versions" scale="log" domain={[2, xMax]}
                  allowDataOverflow
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }} stroke="rgba(255,255,255,0.25)"
                  label={{ value: "Distinct versions in estate (log)", position: "insideBottom", offset: -16, fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
                />
                <YAxis
                  type="number" dataKey="devices_not_on_latest" name="Devices behind" domain={[0, yMax]}
                  tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }} stroke="rgba(255,255,255,0.25)"
                  label={{ value: "Devices not on latest", angle: -90, position: "insideLeft", fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
                />
                <ZAxis type="number" dataKey="total_devices" range={[40, 700]} name="Installs" />
                <ReferenceLine x={xSplit} stroke="rgba(255,255,255,0.45)" strokeDasharray="5 4" />
                <ReferenceLine y={ySplit} stroke="rgba(255,255,255,0.45)" strokeDasharray="5 4" />
                <Tooltip content={<QuadrantTooltip />} cursor={{ strokeDasharray: "3 3", stroke: "rgba(255,255,255,0.3)" }} />
                <Scatter data={plotClean} fill={COLOR_CLEAN} fillOpacity={0.55} stroke={COLOR_CLEAN} strokeOpacity={0.9}
                  onClick={onPointClick} />
                <Scatter data={plotVuln} fill={COLOR_VULN} fillOpacity={0.6} stroke={COLOR_VULN} strokeOpacity={0.9}
                  onClick={onPointClick} />
              </ScatterChart>
            </ResponsiveContainer>
          )}
          <div className="flex items-center gap-4 text-[11px] text-white/60 mt-1">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_VULN }} /> contains a vulnerable version</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR_CLEAN }} /> no vulnerable version</span>
            {plotted.length < filtered.length && <span className="ml-auto">showing top {plotted.length} of {filtered.length}</span>}
          </div>
        </div>

        {/* Drill-down */}
        <div className={`col-span-12 xl:col-span-5 min-w-0 ${CARD_BG} p-4`} data-testid="sprawl-detail-card">
          {!selected && (
            <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center text-sm text-white/55 px-6">
              <Layers className="w-8 h-8 mb-2 text-white/30" />
              Click a bubble or a row to see how a family's devices are spread across its versions.
            </div>
          )}
          {selected && (
            <>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-white truncate">{selected}</div>
                  <div className="text-xs text-white/55">
                    {detail ? (
                      <>
                        {detail.version_count} versions · latest <span className="font-mono">{detail.latest_version || "—"}</span>
                        {" · "}{pct(detail.fragmentation)} fragmented
                        {detail.quadrant && (
                          <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${QUADRANT_META[detail.quadrant]?.chip}`}>
                            {QUADRANT_META[detail.quadrant]?.label}
                          </span>
                        )}
                      </>
                    ) : "Loading…"}
                  </div>

                </div>
                <Link
                  to={`/discover/governance/${encodeURIComponent(selected)}`}
                  className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 shrink-0"
                >
                  Governance <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              {detail && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    ["On latest", detail.devices_on_latest, "text-emerald-300"],
                    ["Behind", detail.devices_not_on_latest, "text-amber-300"],
                    ["Vulnerable", detail.vulnerable_device_count, detail.vulnerable_device_count ? "text-red-300" : "text-white/60"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="rounded-md bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)] p-2">
                      <div className="text-[10px] uppercase tracking-wide text-white/55">{l}</div>
                      <div className={`text-lg font-semibold tabular-nums ${c}`}>{(v || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
              {detailLoading && <div className="text-sm text-white/55 py-10 text-center">Loading…</div>}
              {detail && histogram.length > 0 && (
                <>
                  <div className="text-[11px] text-white/55 mb-1">Devices per version, oldest → newest</div>
                  <ResponsiveContainer width="100%" height={Math.max(220, Math.min(360, 22 * histogram.length))}>
                    <BarChart data={histogram} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 8 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 10 }} stroke="rgba(255,255,255,0.2)" />
                      <YAxis type="category" dataKey="label" width={110} tick={{ fill: "rgba(255,255,255,0.8)", fontSize: 10, fontFamily: "ui-monospace, monospace" }} stroke="rgba(255,255,255,0.2)" interval={0} />
                      <Tooltip content={<HistogramTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                      <Bar dataKey="device_count" radius={[0, 3, 3, 0]} isAnimationActive={false}>
                        {histogram.map((v, i) => (
                          <Cell key={i} fill={v.is_vulnerable ? COLOR_VULN : COLOR_CLEAN} fillOpacity={v.version === detail.latest_version ? 1 : 0.65} />
                        ))}
                        <LabelList dataKey="device_count" position="right" style={{ fill: "rgba(255,255,255,0.75)", fontSize: 10 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {detail.has_vulnerable && (
                    <div className="text-[11px] text-red-300/90 mt-1">
                      Red bars are vulnerable builds — this family is in the Fix Queue.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className={`${CARD_BG} overflow-hidden`} data-testid="sprawl-leaderboard">
        <div className="p-4 flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.08]">
          <div className="text-sm font-semibold text-white">
            Sprawl leaderboard <span className="text-white/50 font-normal">({filtered.length.toLocaleString()} families)</span>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-xs text-white/60 uppercase tracking-wide mr-1 inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Sort</span>
            {Object.entries(SORT_KEYS).map(([k, l]) => (
              <Button key={k} size="sm" variant={sortKey === k ? "default" : "outline"} onClick={() => setSortKey(k)}
                className={sortKey === k ? "bg-cyan-600 hover:bg-cyan-500 text-white" : BTN_GHOST} data-testid={`sprawl-sort-${k}`}>
                {l}
              </Button>
            ))}
          </div>
        </div>
        <div className="max-h-[640px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                <TableHead className="text-white font-semibold">Family</TableHead>
                <TableHead className="text-white font-semibold w-[90px] text-right">Versions</TableHead>
                <TableHead className="text-white font-semibold w-[200px]">Spread</TableHead>
                <TableHead className="text-white font-semibold w-[110px] text-right">Fragmented</TableHead>
                <TableHead className="text-white font-semibold w-[150px]">Quadrant</TableHead>
                <TableHead className="text-white font-semibold w-[90px] text-right">Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data && (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-white/70">Loading…</TableCell></TableRow>
              )}
              {data && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-white/60">No families match the current filters.</TableCell></TableRow>
              )}
              {filtered.slice(0, 300).map((r) => {
                const q = QUADRANT_META[r.quadrant] || QUADRANT_META.healthy;
                const isSel = r.family_name === selected;
                return (
                  <TableRow
                    key={r.family_name}
                    className={`border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04] cursor-pointer ${isSel ? "bg-cyan-500/10" : ""}`}
                    onClick={() => openFamily(r.family_name)}
                    data-testid={`sprawl-row-${r.family_name}`}
                  >
                    <TableCell>
                      <div className="font-medium text-white flex items-center gap-2 flex-wrap">
                        {r.family_name}
                        {r.has_vulnerable && (
                          <Badge className={`${SEVERITY_COLOR[r.worst_severity] || "bg-red-600 text-white"} text-[10px]`}
                            title={`${r.vulnerable_version_count} vulnerable version(s) on ${r.vulnerable_device_count} device(s)`}>
                            {r.worst_severity || "VULNERABLE"}
                          </Badge>
                        )}
                      </div>
                      {r.publisher && <div className="text-[11px] text-white/45 truncate max-w-[40ch]">{r.publisher}</div>}
                    </TableCell>
                    <TableCell className="text-right text-white/90 font-medium tabular-nums">
                      {r.version_count}
                      <div className="text-[10px] text-white/45">{r.version_debt} to retire</div>
                    </TableCell>
                    <TableCell><SpreadBar row={r} /></TableCell>
                    <TableCell className="text-right text-white/85 tabular-nums">{pct(r.fragmentation)}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${q.chip}`} title={q.blurb}>{q.label}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-white tabular-nums">{r.sprawl_score}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filtered.length > 300 && (
            <div className="px-4 py-2 text-[11px] text-white/50 border-t border-white/[0.08]">
              Showing the top 300 of {filtered.length.toLocaleString()} — narrow with the quadrant filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstateSprawlTab;
